import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useRef } from 'react';
import { Column } from 'react-data-grid';
import { FaInfinity } from 'react-icons/fa';
import IssueBadge from '../../common/components/IssueBadge';
import MultiLineEllipsis from '../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { onIssueChipClick } from '../actions/applications-filters-actions';
import {
  ApplicationRow,
  ApplicationsStoreCategories,
} from '../applications-types';
import BusinessPriorityTooltip from '../components/ApplicationsTable/BusinessPriorityTooltip';
import CategoryItem from '../components/ApplicationsTable/CategoryItem';
import AllCheckboxContainer from '../containers/AllCheckboxContainer';
import RowCheckbox from '../containers/RowCheckboxContainer';

export const useApplicationHeader = (
  categories: ApplicationsStoreCategories,
  classes?: {
    textCenter?: string;
    flexCenter?: string;
    headerCell?: string;
    noPadding?: string;
    justifyStart?: string;
    textAlignStart?: string;
  },
) => {
  const baseColumn: Partial<Column<ApplicationRow>> = {
    headerCellClass: [classes?.headerCell, classes?.flexCenter].join(' '),
    cellClass: classes?.flexCenter,
  };

  const columnsRef = useRef<Column<ApplicationRow>[]>();

  if (!columnsRef.current) {
    const columns: Column<ApplicationRow>[] = [
      {
        name: '',
        key: 'checkbox',
        frozen: true,
        formatter: ({ row }) => (
          <RowCheckbox appId={row.appId} isFakeApp={row.fakeApp} />
        ),
        headerRenderer: () => <AllCheckboxContainer />,
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        cellClass: classes?.noPadding,
      },
    ];

    columns.push(
      {
        ...baseColumn,
        name: 'App Name',
        key: 'AppName',
        frozen: true,
        minWidth: 200,
        maxWidth: 350,
        cellClass: undefined,
        headerCellClass: classes?.headerCell,
        formatter: ({
          row: { appName, shortName, repoIcon: RepoIcon, pipeline },
        }) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {RepoIcon && (
                <RepoIcon
                  style={{ width: 'auto', height: 15, flexShrink: 0 }}
                />
              )}
              <MultiLineEllipsis
                lines={2}
                variant='body2'
                color='text.primary'
                title={appName}>
                {shortName}
              </MultiLineEllipsis>
              {pipeline && (
                <Tooltip
                  title={
                    <Typography variant='body2' color='text.primary'>
                      OX Pipeline Enabled
                    </Typography>
                  }>
                  <IconButton
                    size='small'
                    style={{ backgroundColor: 'transparent' }}>
                    <FaInfinity size={15} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        ...baseColumn,
        name: 'Highest Severity Issues',
        key: 'Severities',
        frozen: true,
        maxWidth: 120,
        width: 120,
        minWidth: 120,
        headerCellClass: [classes?.headerCell, classes?.textAlignStart].join(
          ' ',
        ),
        cellClass: classes?.justifyStart,
        formatter: ({ row }) => (
          <IssueBadge
            severitiesAlerts={row.issuesBySeverity}
            fontSize='0.58rem'
            top={3}
            handleClick={onIssueChipClick}
            tooltipPlacement='top'
          />
        ),
      },
      {
        ...baseColumn,
        name: 'Business Priority',
        key: 'BusinessPriority',
        frozen: true,
        maxWidth: 75,
        width: 75,
        minWidth: 75,
        formatter: ({ row }) => (
          <Tooltip
            arrow
            title={
              <BusinessPriorityTooltip
                codeChanges={row.codeChanges}
                lastCodeChange={row.lastCodeChangeDate}
                userCount={row.userCount}
              />
            }>
            <div>{row.businessPriority}</div>
          </Tooltip>
        ),
      },
    );

    if (categories) {
      columns.push(
        ...categories.map(category => {
          return {
            ...baseColumn,
            name: category.categoryName,
            key: `${category.catId}`,
            minWidth: 60,
            formatter: ({ row }) => (
              <CategoryItem
                appName={row.appName}
                category={row.categoriesMap[category.categoryName]}
              />
            ),
          } as Column<ApplicationRow>;
        }),
      );
    }

    columnsRef.current = columns;
  }

  return columnsRef.current;
};
